import {
  LedgerEntry,
  InvoiceEntry,
  PaymentEntry,
  Patient,
} from '@dataTypes/billing.types'
import type {
  PatientARAnalytics,
  ARAgingSummaryReport,
  SidePanelAnalytics,
} from '@services/api/billing/analytics.api'
import type {
  CreateCashPaymentEntryRequest,
  PaymentQueryFilters,
} from '@services/api/billing/payment.api'
import type {
  TransactionEntry,
  TransactionQueryFilters,
} from '@services/api/billing/transaction.api'
import type { LedgerQueryFilters } from '@services/api/billing/ledger.api'
import {
  getInvoiceEntries,
  createInvoiceEntry as createInvoiceEntryApi,
  deleteInvoiceEntry as deleteInvoiceEntryApi,
  type CreateInvoiceEntryRequest,
} from '@services/api/billing/invoice.api'
import { getLedgerEntries } from '@services/api/billing/ledger.api'
import {
  getPatientARAnalytics,
  getInsuranceARAnalytics,
  getARAging,
  getSidePanelAnalytics,
} from '@services/api/billing/analytics.api'
import {
  getPaymentEntries,
  createCashPaymentEntry as createCashPaymentEntryApi,
} from '@services/api/billing/payment.api'
import { getTransactionEntries } from '@services/api/billing/transaction.api'
import { GetAllPatients } from '@services/api/patient.api'
import {
  mapPatientsFromApi,
  type RawPatientFromApi,
} from '@services/api/billing/billingMappers'

const DEFAULT_LEDGER_MESSAGE = 'Failed to fetch ledger entries'
const DEFAULT_PATIENT_AR_MESSAGE = 'Failed to fetch patient AR analytics'
const DEFAULT_INSURANCE_AR_MESSAGE = 'Failed to fetch insurance AR analytics'
const DEFAULT_AR_AGING_MESSAGE = 'Failed to fetch AR aging report'
const DEFAULT_SIDE_PANEL_ANALYTICS_MESSAGE =
  'Failed to fetch side panel analytics'
const DEFAULT_INVOICES_MESSAGE = 'Failed to fetch invoice entries'
const DEFAULT_PAYMENT_HISTORY_MESSAGE =
  'Failed to fetch payment history entries'
const DEFAULT_CREATE_INVOICE_MESSAGE = 'Failed to create invoice entry'
const DEFAULT_DELETE_INVOICE_MESSAGE = 'Failed to delete invoice entry'
const DEFAULT_RECORD_PAYMENT_MESSAGE = 'Failed to create payment entry'
const DEFAULT_TRANSACTIONS_MESSAGE = 'Failed to fetch transaction entries'

export type BillingLedgerFilters = {
  patient_id?: string
  clinic_id?: string
  provider_id?: string | string[]
  search?: string
  limit?: number
  offset?: number
  sort?: string
}

export type BillingInvoiceFilters = {
  patient_id?: string
  clinic_id?: string
  invoice_status?: string | string[]
  search?: string
  limit?: number
  offset?: number
  sort?: string
}

export type BillingPaymentFilters = {
  patient_id?: string
  clinic_id?: string
  payment_type?: string | string[]
  search?: string
  limit?: number
  offset?: number
  sort?: string
}

export async function fetchLedger(
  filters?: BillingLedgerFilters,
): Promise<{ entries: LedgerEntry[]; count: number }> {
  const response = await getLedgerEntries(
    filters as LedgerQueryFilters | undefined,
  )
  if (response.statusCode !== 200) {
    throw new Error(response.message || DEFAULT_LEDGER_MESSAGE)
  }
  const rawEntries = Array.isArray(response.data?.entries)
    ? response.data.entries
    : []
  const count = response.data?.count ?? 0
  const entries = (rawEntries as LedgerEntry[]).map((e) => ({ ...e }))
  return { entries, count }
}

export async function fetchPatientARAnalytics(
  patientId?: string,
  clinicId?: string,
): Promise<PatientARAnalytics> {
  const response = await getPatientARAnalytics(patientId, clinicId)
  if (response.statusCode !== 200) {
    throw new Error(response.message || DEFAULT_PATIENT_AR_MESSAGE)
  }
  const analytics = response.data?.analytics
  if (!analytics) {
    throw new Error('No analytics data received')
  }
  return analytics
}

export async function fetchInsuranceARAnalytics(
  patientId?: string,
  clinicId?: string,
): Promise<PatientARAnalytics> {
  const response = await getInsuranceARAnalytics(patientId, clinicId)
  if (response.statusCode !== 200) {
    throw new Error(response.message || DEFAULT_INSURANCE_AR_MESSAGE)
  }
  const analytics = response.data?.analytics
  if (!analytics) {
    throw new Error('No analytics data received')
  }
  return analytics
}

export async function fetchARAging(
  patientId?: string,
  clinicId?: string,
): Promise<ARAgingSummaryReport> {
  const response = await getARAging(patientId, clinicId)
  if (response.statusCode !== 200) {
    throw new Error(response.message || DEFAULT_AR_AGING_MESSAGE)
  }
  const report = response.data?.report
  if (!report) {
    throw new Error('No ARaging report data received')
  }
  return report
}

export async function fetchSidePanelAnalytics(
  patientId: string,
  clinicId?: string,
): Promise<SidePanelAnalytics> {
  const response = await getSidePanelAnalytics(patientId, clinicId)
  if (response.statusCode !== 200) {
    throw new Error(response.message || DEFAULT_SIDE_PANEL_ANALYTICS_MESSAGE)
  }
  const analytics = response.data?.analytics
  if (!analytics) {
    throw new Error('No side panel analytics data received')
  }
  return analytics
}

export async function fetchPatientsForBilling(): Promise<Patient[]> {
  const response = await GetAllPatients()
  if (response.statusCode !== 200) {
    throw new Error(response.message || 'Failed to fetch patients for billing')
  }
  const raw = response.data?.patients
  return mapPatientsFromApi(raw as RawPatientFromApi[] | undefined)
}

export async function fetchInvoices(params: BillingInvoiceFilters): Promise<{
  entries: InvoiceEntry[]
  count: number
}> {
  const response = await getInvoiceEntries(params)
  if (response.statusCode !== 200) {
    throw new Error(response.message || DEFAULT_INVOICES_MESSAGE)
  }
  const rawEntries = Array.isArray(response.data?.entries)
    ? response.data.entries
    : []
  const count = response.data?.count ?? 0
  const entries = (rawEntries as InvoiceEntry[]).map((e) => ({ ...e }))
  return { entries, count }
}

export async function fetchPaymentHistory(
  filters?: BillingPaymentFilters,
): Promise<{ entries: PaymentEntry[]; count: number }> {
  const response = await getPaymentEntries(
    (filters as PaymentQueryFilters) || undefined,
  )
  if (response.statusCode !== 200) {
    throw new Error(response.message || DEFAULT_PAYMENT_HISTORY_MESSAGE)
  }
  const entries = Array.isArray(response.data?.entries)
    ? response.data.entries
    : []
  const count = response.data?.count ?? 0
  return { entries, count }
}

export async function createInvoice(
  payload: CreateInvoiceEntryRequest,
): Promise<void> {
  const response = await createInvoiceEntryApi(payload)
  if (response.statusCode < 200 || response.statusCode >= 300) {
    throw new Error(response.message || DEFAULT_CREATE_INVOICE_MESSAGE)
  }
}

export async function deleteInvoice(invoiceId: string): Promise<void> {
  const response = await deleteInvoiceEntryApi(invoiceId)
  if (response.statusCode < 200 || response.statusCode >= 300) {
    throw new Error(response.message || DEFAULT_DELETE_INVOICE_MESSAGE)
  }
}

export async function createCashPayment(
  payload: CreateCashPaymentEntryRequest,
): Promise<void> {
  const response = await createCashPaymentEntryApi(payload)
  if (response.statusCode < 200 || response.statusCode >= 300) {
    throw new Error(response.message || DEFAULT_RECORD_PAYMENT_MESSAGE)
  }
}

export async function fetchTransactions(
  queryFilters: TransactionQueryFilters,
): Promise<{ entries: TransactionEntry[]; count: number }> {
  const response = await getTransactionEntries(queryFilters)
  if (response.statusCode !== 200) {
    throw new Error(response.message || DEFAULT_TRANSACTIONS_MESSAGE)
  }
  const rawEntries = Array.isArray(response.data?.entries)
    ? response.data.entries
    : []
  const count = response.data?.count ?? 0
  const entries = (rawEntries as TransactionEntry[]).map((e) => ({ ...e }))
  return { entries, count }
}
